import { useEffect, useRef, useState, type RefObject } from 'react';

import { ConsentGuard } from '@hivebrite/design-system/Embed';

import { type Replacement } from 'hb-react/shared/components/HtmlContent/replacementEngine';
import { getReplacementsKey } from 'hb-react/shared/components/HtmlContent/replacementEngine/utils';
import { extractConsentGuardProps } from 'hb-react/shared/components/HtmlContent/utils/extractConsentGuardProps';
import { parseCssString } from 'hb-react/shared/components/HtmlContent/utils/parseCssString';
import { ImageWithPreview } from 'hb-react/shared/components/ImageWithPreview';

interface Portal {
  element: JSX.Element;
  key: string;
  target: Element;
}

interface UseReplacementRendererProps {
  containerRef: RefObject<HTMLElement>;
  enableImagePreview?: boolean;
  replacements: Array<Replacement> | undefined;
}

/**
 * Custom hook that renders replacement elements as React portals
 *
 * @param replacements - Array of processed replacements from the replacement engine
 * @param containerRef - Reference to the container element where replacements will be rendered
 * @returns Array of portal objects for rendering
 */
export const useReplacementRenderer = ({
  replacements,
  containerRef,
  enableImagePreview = false,
}: UseReplacementRendererProps): Array<Portal> => {
  const [portals, setPortals] = useState<Array<Portal>>([]);
  const previousReplacementsRef = useRef<string>('');

  useEffect(() => {
    const container = containerRef.current;

    if (!container || !replacements) {
      // Only clear portals if we had replacements before (to avoid setting state unnecessarily)
      if (previousReplacementsRef.current !== '') {
        setPortals([]);
        previousReplacementsRef.current = '';
      }
      return;
    }

    // Create a stable key from replacements to compare if they actually changed
    // This prevents infinite loops when replacements array gets a new reference
    // but contains the same content.
    const replacementsKey = getReplacementsKey(replacements);

    // Skip if replacements haven't actually changed
    if (previousReplacementsRef.current === replacementsKey) {
      return;
    }

    previousReplacementsRef.current = replacementsKey;

    const newPortals: Array<Portal> = [];

    replacements.forEach((replacement) => {
      const span = container.querySelector(`#${replacement.spanId}`);

      if (!span) {
        // eslint-disable-next-line no-console
        console.warn(
          `Target element not found for replacement: ${replacement.spanId}`,
        );
        return;
      }

      let element: JSX.Element;

      if (replacement.type === 'iframe') {
        const { hostName, providerName, aspectRatio } =
          extractConsentGuardProps(replacement.iframe);

        element = (
          <ConsentGuard
            hostName={hostName}
            providerName={providerName}
            aspectRatio={aspectRatio}
            isActive
          >
            {/* eslint-disable-next-line react/no-danger */}
            <div
              dangerouslySetInnerHTML={{ __html: replacement.iframe.outerHTML }}
            />
          </ConsentGuard>
        );
      } else if (replacement.type === 'image') {
        // Respect enableImagePreview prop and disable preview if image is wrapped in a link
        const enablePreview =
          enableImagePreview && !replacement.isWrappedInLink;

        element = (
          <ImageWithPreview
            src={replacement.src}
            alt={replacement.alt}
            width={
              replacement.width ? parseInt(replacement.width, 10) : undefined
            }
            height={
              replacement.height ? parseInt(replacement.height, 10) : undefined
            }
            style={
              replacement.style ? parseCssString(replacement.style) : undefined
            }
            enablePreview={enablePreview}
          />
        );
      } else {
        // Skip unknown replacement types
        // eslint-disable-next-line no-console
        console.warn(
          `Unknown replacement type: ${(replacement as Replacement).type}`,
        );
        return;
      }

      newPortals.push({
        element,
        target: span,
        key: replacement.spanId,
      });
    });

    setPortals(newPortals);
  }, [replacements, containerRef, enableImagePreview]);

  return portals;
};
